/**
 * Matriz de permisos por rol y pantalla del panel.
 *
 * Es el ÚNICO lugar donde se decide quién ve qué: lo consumen el menú lateral
 * (`Layout.tsx`), los guardas de ruta (`router.tsx`) y las pantallas que esconden sus
 * botones de edición. Antes cada archivo repetía su propio `roles.includes('admin')` y por
 * eso el coordinador terminaba viendo "Cierres" y "Ajustes", que solo le muestran un muro.
 *
 * ESTO NO ES SEGURIDAD: la seguridad real es la RLS de Postgres (un menú escondido no
 * impide una llamada directa a la API). Esto es *encuadre*: que cada rol vea la pantalla
 * que le sirve y no pierda tiempo con la que le va a rebotar.
 *
 * Ámbito de cada rol:
 * - `admin` (Neri, Roberto): aprueba y configura. Edita todo.
 * - `comercial`: trae la campaña y carga la programación. No asigna ni valida.
 * - `coordinador`: David coordina MAKRO; Miguel Ángel y Alanish supervisan con el MISMO rol.
 *   Lo que los separa es de qué campañas son equipo (`campaign_staff`), y eso lo resuelve
 *   la RLS fila por fila — no el menú, que es idéntico para los dos.
 * - `recurso`: no entra al panel; su app es `/movil`.
 * - `supervisor` (0110): quien recorre la ruta. Su trabajo es `/movil` («Mi ruta») y ahí aterriza.
 *   Desde el 25-set (0218) además VE cuatro pantallas del panel —Hoy, Planificación, Asignaciones y
 *   Registros de campo— **solo de sus tiendas** (`campaign_pdv_supervisors`) y **sin accionar**:
 *   `ver`, nunca `editar`. El recorte por tienda lo hace la RLS, no esta matriz. Reemplaza al atajo de
 *   darles `admin` para que vieran algo, que además les daba datos bancarios y cambiar roles.
 *
 *   Existía como la bandera `profiles.hace_ruta` y no como rol, así que no se podía ni ver ni
 *   marcar desde el panel: había que encenderla por SQL. El usuario pidió el 11-set poder
 *   identificarlos —*«necesito identificar a los supervisores […] en base a eso, asignaremos los
 *   formularios solo a ellos»*— y ahí se hizo rol. La bandera sigue siendo la fuente de verdad del
 *   servidor —`hace_ruta()` gobierna «Mi ruta» y la inserción de fichas de campo— y un trigger la
 *   enciende sola cuando se marca el rol: una casilla, un solo lugar donde mirar.
 * - `datos` (0061, ensanchado el 2026-09-10): el validador. VE todas las campañas —presentes y
 *   futuras— y CORRIGE lo que el campo registró, sin tocar planilla, configuración ni datos
 *   bancarios. No se resolvió con `coordinador` porque ese está atado a SUS campañas y, además,
 *   corregir está restringido al coordinador DESIGNADO: habría visto todo sin poder arreglar nada.
 *
 *   El 10-set el usuario pidió *«dale visibilidad de las mismas pestañas que ven los coordinadores
 *   … es el encargado de la revisión de la data»*, así que las cinco pantallas que le faltaban
 *   —asignaciones, aprobaciones, trabajadores, documentos, autorizaciones— pasaron a `ver`.
 *
 *   **`ver` y no `coordinador`, y la diferencia es el punto.** Darle el rol era una línea y le
 *   habría regalado cosas que nadie pidió: resetear la clave de cualquier trabajador de campo
 *   —`puede_gestionar_claves()` pasa por encima de `can_manage_credentials`, y con la clave puesta
 *   se puede entrar y marcar asistencia a nombre de esa persona—, editar los 55 cierres de turno
 *   sin motivo ni rastro por `impulso_reports_update_staff`, mover el GPS de los 101 PDV, y
 *   aprobar o rechazar postulantes, que es contratar. Además le habría subido `validacion` de
 *   `ver` a `editar`, contestando sola la pregunta abierta U17. La 0105 ensanchó la LECTURA del
 *   servidor y esta matriz la acompaña; nada de escritura. Ver BITACORA §59.
 * - `direccion` (0394, 3-nov): Francisco Sepúlveda, dueño de la empresa, que entra a ver que el sistema funciona.
 *   VE el resumen gerencial —con el que entra— y nueve pantallas más, y **no edita ninguna**: la lista la dio el usuario y no se ensancha sin
 *   preguntar. En el servidor lee lo mismo que `datos` más los cierres de pago, con políticas SELECT propias; no se
 *   le sumó a `can_view_campaign` porque esa función también decide escrituras. Nunca ve cuentas bancarias.
 */

export type Rol =
  | 'admin' | 'comercial' | 'coordinador' | 'recurso' | 'datos' | 'supervisor' | 'comprador' | 'solicitante'
  | 'direccion';

export type Pantalla =
  | 'resumen'
  | 'hoy'
  | 'planificacion'
  | 'campanias'
  | 'importar'
  | 'asignaciones'
  | 'aprobaciones'
  | 'trabajadores'
  | 'documentos'
  | 'autorizaciones'
  | 'pdv'
  | 'validacion'
  | 'canjes'
  // ⚠️ SOFTYS, en pausa y oculto del menú desde el 5-set. NO es la de abajo.
  | 'levantamiento'
  | 'levantamientoPg'
  | 'cierres'
  | 'academia'
  | 'ajustes';

/** `ver` = la pantalla le sirve para leer; `editar` = además puede accionar en ella. */
export type Acceso = 'nada' | 'ver' | 'editar';

const RANGO: Record<Acceso, number> = { nada: 0, ver: 1, editar: 2 };

const TODOS_LOS_ROLES: readonly Rol[] =
  ['admin', 'comercial', 'coordinador', 'recurso', 'datos', 'supervisor', 'comprador', 'solicitante', 'direccion'];

/**
 * Lo que NO aparece aquí es `nada`. El jefe de operaciones edita todo, así que se omite
 * de la tabla y se agrega al leerla: si mañana nace una pantalla nueva, el jefe la ve por
 * defecto y el olvido no lo deja fuera de su propio sistema.
 */
const MATRIZ: Record<Pantalla, Partial<Record<Rol, Acceso>>> = {
  // El resumen gerencial dentro del panel (0396): la pantalla con la que entra dirección. El servidor lo respalda
  // (`resumen_gerencial_panel` solo deja pasar a admin y direccion).
  resumen: { direccion: 'ver' },
  // Tablero del día: los tres roles de oficina lo leen, nadie acciona desde ahí.
  hoy: { comercial: 'ver', coordinador: 'ver', datos: 'ver', supervisor: 'ver', direccion: 'ver' },
  // El pedido explícito del usuario: el coordinador y el supervisor VEN la planificación.
  // Para el comercial es `editar` porque desde ahí salta al importador.
  planificacion: { comercial: 'editar', coordinador: 'ver', datos: 'ver', supervisor: 'ver', direccion: 'ver' },
  // El coordinador lee la ficha de su campaña (fechas, horarios, tarifas) para saber qué
  // tiene que cubrir; quien la edita es el comercial dueño (RLS: `owns_campaign`).
  campanias: { comercial: 'editar', coordinador: 'ver', datos: 'ver', direccion: 'ver' },
  importar: { comercial: 'editar' },
  // Asignar y reemplazar es del coordinador; al comercial no le toca y solo le estorba.
  // `datos` la LEE (10-set): la programación es la mitad del cruce que hace el validador —quién
  // debía estar dónde contra qué se registró—. La pantalla entra en modo lectura: sin «Asignar»,
  // «Cambiar recurso», «Retirar», «Desasignar» ni la solapa de carga masiva. El servidor lo
  // respalda igual (`manages_campaign` pide campaña designada o `campaign_staff`, y él no tiene
  // ninguna), así que mostrarle esos botones sería ofrecerle un rechazo.
  asignaciones: { coordinador: 'editar', datos: 'ver', supervisor: 'ver', direccion: 'ver' },
  // Pedido del usuario (2026-08-12): el coordinador aprueba las propuestas que suben sus
  // supervisores, para que una activación no espere a que el jefe se acuerde. La pantalla
  // tiene tres solapas y al coordinador se le muestra SOLO "Asignaciones": las otras dos son
  // crear cuentas de trabajadores y aprobar cierres de PAGO, que siguen siendo del jefe.
  // El servidor respalda exactamente esto (0026): solo el coordinador DESIGNADO de la campaña
  // aprueba, no cualquiera con el rol — los supervisores comparten rol y aprobarían lo suyo.
  //
  // `datos` la LEE (10-set): le sirve para saber qué turnos todavía NO están confirmados, que es
  // información de revisión. Aprobar y rechazar quedan escondidos: `approve_assignments` exige ser
  // el coordinador designado.
  aprobaciones: { coordinador: 'editar', datos: 'ver', direccion: 'ver' },
  /**
   * El coordinador y el supervisor LEEN el padrón de trabajadores. Pedido del usuario del
   * 2026-09-04: no lo tenían y lo necesitan para dos cosas concretas que ya les tocan —saber quién
   * de su equipo todavía no ha entrado al sistema y mandarle su clave por WhatsApp (0085/0086)—.
   *
   * `ver`, no `editar`, y la diferencia importa: dar de alta, cambiar roles, cambiar el estado de
   * una persona y aprobar o rechazar solicitudes siguen siendo del jefe de operaciones. Esta
   * pantalla es la única que junta las tres cosas, así que la solapa «Activos» se muestra y las de
   * «Solicitudes» e «Importar» no.
   *
   * Los botones de clave NO dependen de esta fila: tienen su propio permiso
   * (`can_manage_credentials` o ser coordinador), y el servidor limita a quién se le puede cambiar.
   */
  trabajadores: { coordinador: 'ver', datos: 'ver', direccion: 'ver' },
  // El coordinador consulta vencimientos de su gente; subir y renovar es del jefe. `datos` los
  // consulta de TODA la gente: un carné vencido explica por qué una tienda no dejó entrar a
  // alguien, y eso aparece como una falta sin motivo en la data que él revisa.
  documentos: { coordinador: 'ver', datos: 'ver' },
  // `datos` las LEE, y es lo que más le cambia el trabajo: un permiso aprobado es la diferencia
  // entre una falta y una ausencia justificada. Aprobarlas sigue siendo del coordinador y del jefe
  // —decidir sobre el permiso de alguien no es revisar data—.
  autorizaciones: { coordinador: 'editar', datos: 'ver' },
  // El catálogo de PDV se lee para entender la programación; se edita solo desde el jefe.
  pdv: { comercial: 'ver', coordinador: 'ver', datos: 'ver', direccion: 'ver' },
  // Desde el 26-set (0228) `datos` y el supervisor también CORRIGEN marcaciones: validar, editar,
  // salida justificada y completar turno. Decisión del usuario. `datos` en todas las campañas; el
  // supervisor solo en sus tiendas (lo recorta el servidor, `corrige_marcas_por_rol`). Borrar una
  // marca sigue siendo solo del admin, y nadie corrige sus propias marcas por esta vía.
  validacion: { coordinador: 'editar', datos: 'editar', supervisor: 'editar', direccion: 'ver' },
  // Bandeja de canjes: el coordinador y el supervisor LEEN lo que registró su equipo (la RLS ya
  // se los permite por `can_view_campaign`), y desde la 0044 el coordinador también CORRIGE Y
  // ANULA — pedido del usuario.
  //
  // ⚠️ `editar` acá NO significa "puede accionar sobre cualquier fila". Esta matriz solo sabe de
  // roles, y el rol `coordinador` lo comparten el coordinador designado y los supervisores de la
  // campaña. Quién puede accionar sobre CADA registro lo decide `puedeAccionarFila` en la pantalla
  // (espejo de `puede_editar_canje` en el servidor): solo el coordinador designado de esa campaña.
  // Sin esa segunda puerta, un supervisor vería botones que el servidor le rechaza.
  canjes: { coordinador: 'editar', datos: 'editar', supervisor: 'ver' },
  // Cierre de pago y parámetros del sistema: del jefe de operaciones y de nadie más.
  // Levantamiento de canal: el coordinador acompaña a su equipo y revisa la calidad del dato
  // (§5.1 del documento: acompañar, no auditar). El comercial no tiene nada que hacer acá.
  levantamiento: { coordinador: 'editar', datos: 'editar' },
  /**
   * Levantamiento de información (menú «Levantamiento inf.»): P&G —mercados y canal moderno— y,
   * desde el 30-set, Yichang —canal moderno, bodegas y mercados—. La clave se sigue llamando
   * `levantamientoPg` porque es un cambio de rótulo, no de estructura (como `canjes`).
   *
   * Solo `admin` y `datos`, y **no es una decisión de encuadre sino de física**: la RLS de
   * `mer_fichas`, `mer_cierres`, `pg_fichas` y de las cuatro `yi_*` de Yichang (0242) exige
   * `has_role('admin')` o `has_role('datos')`. A cualquier otro rol la consulta no le da error: le
   * devuelve CERO filas, así que la pantalla se dibujaría entera y vacía y se leería como «no hay
   * data». Ofrecérsela al coordinador sería regalarle una pantalla que miente.
   *
   * `datos` en `ver` y no `editar` porque esta entrega no escribe nada. Corregir una grafía de
   * mercado exige tabla de correcciones, motivo obligatorio y rastro de quién — otra migración.
   *
   * (`admin` no se lista: se agrega al leer la matriz y edita todo por defecto.)
   */
  levantamientoPg: { datos: 'ver' },
  // Academia (0064): el contenido de capacitación y los resultados por recurso.
  // Decisión del usuario el 27-ago: editan contenido admin Y coordinador. El comercial y
  // `datos` solo miran resultados — no cargan cursos, y un editor que no van a usar les
  // estorba más de lo que les sirve.
  academia: { coordinador: 'editar', comercial: 'ver', datos: 'ver' },
  // Dirección LEE los cierres (montos, tarifas, feriado) y no crea, aprueba ni baja la planilla con cuentas (0394).
  cierres: { direccion: 'ver' },
  ajustes: {},
};

/** Pantallas en el orden en que se muestran en el menú. */
export const PANTALLAS: readonly Pantalla[] = Object.keys(MATRIZ) as Pantalla[];

function accesoDeRol(pantalla: Pantalla, rol: string): Acceso {
  if (rol === 'admin') return 'editar';
  return MATRIZ[pantalla][rol as Rol] ?? 'nada';
}

/** El acceso efectivo: los roles son acumulables, así que manda el mayor de todos. */
export function acceso(pantalla: Pantalla, roles: string[]): Acceso {
  let mayor: Acceso = 'nada';
  for (const rol of roles) {
    const a = accesoDeRol(pantalla, rol);
    if (RANGO[a] > RANGO[mayor]) mayor = a;
  }
  return mayor;
}

export function puedeVer(pantalla: Pantalla, roles: string[]): boolean {
  return acceso(pantalla, roles) !== 'nada';
}

export function puedeEditar(pantalla: Pantalla, roles: string[]): boolean {
  return acceso(pantalla, roles) === 'editar';
}

/* ---------------------------------------------------------------------------
 * Capacidades finas dentro de «Trabajadores»
 *
 * La matriz decide por PANTALLA, y esta junta tres trabajos de peso muy distinto en tres solapas:
 * trabajar la cola de postulantes, administrar el padrón y dar de alta en masa. Al coordinador le
 * toca el primero y no los otros dos (pedido del usuario del 2026-09-04), así que la fila `ver`
 * de la matriz no alcanza para describirlo y estas tres funciones lo completan.
 *
 * Viven ACÁ y no dentro de la pantalla por lo mismo que dice el encabezado del archivo: si cada
 * componente resuelve su propio `roles.includes(...)`, en tres meses hay tres reglas distintas
 * para la misma pregunta. Y son testables sin montar React.
 * ------------------------------------------------------------------------- */

/**
 * Aprobar y rechazar postulantes. El coordinador entra acá (0089).
 *
 * El servidor respalda exactamente esto: policy de `worker_applications` para `pendiente` →
 * `aprobado`/`rechazado`, y `approve-worker` acepta el modo `single` de un coordinador — que solo
 * puede crear perfiles con rol `recurso`, porque la Edge Function lo fija a fuego.
 */
export function puedeAprobarSolicitudes(roles: string[]): boolean {
  return roles.includes('admin') || roles.includes('coordinador');
}

/**
 * Dar de alta, importar en masa, cambiar roles y cambiar el estado de una persona.
 * Administración del padrón: del jefe de operaciones y de nadie más.
 */
export function puedeAdministrarPadron(roles: string[]): boolean {
  return roles.includes('admin');
}

/**
 * Bloquear un documento (lista negra). **Solo admin, y a propósito.**
 *
 * Es una marca reputacional permanente sobre una persona, no un rechazo: le impide registrarse
 * para siempre en cualquier campaña. `block_document()` exige `has_role('admin')` en el servidor,
 * así que esconder el botón acá solo evita ofrecer algo que va a rebotar.
 */
export function puedeBloquearDocumento(roles: string[]): boolean {
  return roles.includes('admin');
}

/**
 * Asignar supervisor a una tienda dentro de una campaña (`campaign_pdv_supervisors`).
 *
 * La pestaña "Supervisores" vive dentro de Campañas, donde hoy el coordinador solo LEE (`ver`
 * en la matriz de `campanias`). Acá se le abre edición a propósito — pedido explícito del
 * usuario (24-set): el coordinador está más cerca del terreno que el comercial para decidir
 * quién supervisa qué tienda. El servidor respalda lo mismo: la RLS de
 * `campaign_pdv_supervisors` acepta `admin`, el comercial dueño de la campaña, o quien la
 * gestiona (coordinador designado o `campaign_staff`).
 */
export function puedeAsignarSupervisores(roles: string[]): boolean {
  return roles.includes('admin') || roles.includes('comercial') || roles.includes('coordinador');
}

/**
 * El supervisor de ruta sin ningún rol de oficina: el que entra al panel SOLO a ver sus tiendas.
 *
 * Sirve para esconderle lo que `ver` no alcanza a esconder: descargas con enlaces a fotos que abren
 * sin sesión durante un año (decisión del usuario, 25-set) y tarjetas que, por la RLS, le mostrarían
 * solo lo suyo como si fuera del equipo. Con cualquier rol de oficina además, se comporta como ese rol.
 */
export function soloSupervisaTiendas(roles: string[]): boolean {
  return roles.includes('supervisor')
    && !['admin', 'comercial', 'coordinador', 'datos'].some((r) => roles.includes(r));
}

/** Los roles con acceso de lectura o más. Es lo que espera `RequireRole` en las rutas. */
export function rolesQueVen(pantalla: Pantalla): Rol[] {
  return TODOS_LOS_ROLES.filter((rol) => puedeVer(pantalla, [rol]));
}

/**
 * Compras, «Cotizaciones Treid» (6 y 7-oct, spec 2026-10-06-compras-design.md §3). El comprador y el solicitante no
 * entran al panel de campo por estos roles: no tienen filas en la MATRIZ, a propósito. Su app es `/cotizaciones/`.
 *
 * Mira el rol y nada más: ser admin del sistema de campo NO abre las compras. Quién es comprador o solicitante lo
 * decide un administrador de compras (trigger `profiles_guard_comprador`, 0272 y 0273), no esta función; esto es
 * encuadre.
 */
export function puedeComprar(roles: string[]): boolean {
  return roles.includes('comprador');
}

/**
 * Quien puede pedir una compra: comprador o solicitante (spec §3.3, fila 5). Decide si el menú del panel muestra el
 * enlace a «Cotizaciones Treid». El solicitante solo ve ahí sus solicitudes; lo frena la base, no el menú.
 */
export function puedeSolicitar(roles: string[]): boolean {
  return roles.includes('comprador') || roles.includes('solicitante');
}

/**
 * Comprador que quedó con `recurso` puesto (la trampa del alta, §3.4): `approve-worker` crea a todos como recurso.
 * Con `admin` no se avisa: `es_trabajador_de_campo` ya lo excluye, y un aviso que nunca se apaga se deja de mirar.
 */
export function compradorConRecurso(roles: string[]): boolean {
  return roles.includes('comprador') && roles.includes('recurso') && !roles.includes('admin');
}
